import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, AlertTriangle, Check, X, Send, CheckCircle, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { sendToWooCommerce } from '../lib/woocommerce';
import { addOrUpdateProduct, updateProductStatus } from '../lib/db';

interface CsvRow {
  id: number;
  client_number: string;
  items: string[];
  total_price: number;
  validated: boolean;
  status?: string;
  error?: string;
}

export function CsvImporter() {
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<CsvRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [delimiter, setDelimiter] = useState<string>(';');
  const [isLoading, setIsLoading] = useState(false);
  const [showFormat, setShowFormat] = useState(false);
  const [textAreaContent, setTextAreaContent] = useState('');
  const [importMethod, setImportMethod] = useState<'file' | 'textarea'>('file');
  const [processingStatus, setProcessingStatus] = useState<{
    isProcessing: boolean;
    current: number;
    total: number;
    success: number;
    failed: number;
  }>({
    isProcessing: false,
    current: 0,
    total: 0,
    success: 0,
    failed: 0
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast.error('Veuillez sélectionner un fichier CSV valide');
        return;
      }
      setFile(selectedFile);
      parseCSV(selectedFile);
    }
  };

  // Fonction pour nettoyer le format
  const cleanItemFormat = (item: string): string => {
    // Supprimer les guillemets doubles et espaces supplémentaires
    return item.replace(/^""|""$/g, '').trim();
  };

  const parseCSVContent = async (text: string) => {
    setIsLoading(true);
    setErrors([]);
    
    try {
      const lines = text.split('\n');
      
      if (lines.length < 1) {
        setErrors(['Le fichier CSV doit contenir au moins une ligne de données']);
        setData([]);
        setIsLoading(false);
        return;
      }
      
      // Détection du délimiteur s'il n'est pas spécifié (virgule ou point-virgule)
      const firstLine = lines[0];
      if (firstLine.includes(';')) {
        setDelimiter(';');
      } else if (firstLine.includes(',')) {
        setDelimiter(',');
      }
      
      const parsedData: CsvRow[] = [];
      const newErrors: string[] = [];
      
      // Parcourir les lignes de données (directement sans considérer la première comme en-tête)
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Ignorer les lignes vides
        
        // Diviser la ligne en utilisant le délimiteur
        const columns = line.split(delimiter);
        
        if (columns.length < 3) {
          newErrors.push(`Ligne ${i+1}: Données incomplètes. Format attendu: client_number;items;total_price`);
          continue;
        }
        
        const clientNumber = columns[0].trim().replace(/"/g, '');
        
        // Simplifier: prendre directement la description comme un seul item
        const itemStr = columns[1].trim().replace(/"/g, '');
        const items = [itemStr];
        
        const priceStr = columns[2].trim().replace(/"/g, '').replace('€', '');
        
        if (!clientNumber) {
          newErrors.push(`Ligne ${i+1}: Numéro de client manquant.`);
          continue;
        }
        
        if (itemStr.length === 0) {
          newErrors.push(`Ligne ${i+1}: Description manquante.`);
          continue;
        }
        
        const price = parseFloat(priceStr);
        if (isNaN(price)) {
          newErrors.push(`Ligne ${i+1}: Prix invalide - "${priceStr}".`);
          continue;
        }
        
        parsedData.push({
          id: i,
          client_number: clientNumber,
          items: items,
          total_price: price,
          validated: true
        });
      }
      
      setData(parsedData);
      setErrors(newErrors);
      
      if (parsedData.length === 0) {
        toast.error('Aucune donnée valide trouvée dans le fichier CSV');
      } else if (newErrors.length > 0) {
        toast.warning(`${parsedData.length} lignes importées avec ${newErrors.length} erreurs`);
      } else {
        toast.success(`${parsedData.length} lignes importées avec succès`);
      }
    } catch (error: any) {
      console.error('Erreur lors du parsing du CSV:', error);
      setErrors([`Erreur lors de l'analyse du fichier: ${error.message || 'Format non valide'}`]);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const parseCSV = async (csvFile: File) => {
    const text = await csvFile.text();
    await parseCSVContent(text);
  };

  const parseTextAreaContent = async () => {
    if (!textAreaContent.trim()) {
      toast.error('Veuillez entrer des données CSV');
      return;
    }
    await parseCSVContent(textAreaContent);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length) {
      const droppedFile = files[0];
      if (droppedFile.type !== 'text/csv' && !droppedFile.name.endsWith('.csv')) {
        toast.error('Veuillez déposer un fichier CSV valide');
        return;
      }
      setFile(droppedFile);
      parseCSV(droppedFile);
    }
  };

  // Utiliser useCallback pour les fonctions de gestion d'événements fréquents
  const toggleRowValidation = useCallback((id: number) => {
    setData(prevData => 
      prevData.map(row => 
        row.id === id ? {...row, validated: !row.validated} : row
      )
    );
  }, []);

  const handleSendToWooCommerce = async () => {
    const validatedItems = data.filter(row => row.validated);
    
    if (validatedItems.length === 0) {
      toast.error('Aucun élément sélectionné pour l\'envoi');
      return;
    }
    
    setProcessingStatus({
      isProcessing: true,
      current: 0,
      total: validatedItems.length,
      success: 0,
      failed: 0
    });
    
    const updatedData = [...data];
    
    for (let i = 0; i < validatedItems.length; i++) {
      const item = validatedItems[i];
      const index = data.findIndex(row => row.id === item.id);
      
      try {
        setProcessingStatus(prev => ({
          ...prev,
          current: i + 1
        }));
        
        // D'abord ajouter le produit dans notre base de données
        const productData = {
          clientNumber: item.client_number,
          category: '',
          note: item.items.join('\n'),
          price: item.total_price.toString()
        };
        
        await addOrUpdateProduct(productData);
        
        // Récupérer l'ID du produit (on devrait le récupérer de addOrUpdateProduct)
        // Pour simplifier, on utilise un ID temporaire pour cet exemple
        
        // Puis envoyer à WooCommerce
        const result = await sendToWooCommerce({
          id: -1, // ID temporaire
          client_number: item.client_number,
          items: JSON.stringify(item.items),
          total_price: item.total_price,
          status: 'completed'
        });
        
        // Si l'envoi est réussi, marquer le produit comme complété pour le supprimer de la page d'accueil
        if (result && result.id) {
          // Cette étape est normalement gérée par sendToWooCommerce,
          // mais on ajoute une sécurité supplémentaire ici
          await updateProductStatus(-1, 'completed');
        }
        
        updatedData[index] = {
          ...updatedData[index],
          status: 'success'
        };
        
        setProcessingStatus(prev => ({
          ...prev,
          success: prev.success + 1
        }));
      } catch (error: any) {
        console.error(`Erreur lors de l'envoi de l'élément ${item.client_number}:`, error);
        let errorMessage = error.message || 'Erreur inconnue';
        
        // Gestion spécifique des erreurs de mise à jour de l'ID WooCommerce
        if (errorMessage.includes('l\'ID WooCommerce')) {
          errorMessage = 'Produit envoyé mais erreur de mise à jour de référence';
        }
        
        updatedData[index] = {
          ...updatedData[index],
          status: 'error',
          error: errorMessage
        };
        
        setProcessingStatus(prev => ({
          ...prev,
          failed: prev.failed + 1
        }));
      }
      
      setData(updatedData);
    }
    
    setProcessingStatus(prev => ({
      ...prev,
      isProcessing: false
    }));
    
    if (processingStatus.failed === 0) {
      toast.success('Tous les produits ont été envoyés avec succès !');
    } else {
      toast.error(`${processingStatus.success} produits envoyés, ${processingStatus.failed} échecs`);
    }
  };

  const handleReset = () => {
    setFile(null);
    setData([]);
    setErrors([]);
    setTextAreaContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const csvTemplate = `3428;Robe 10;10.00
70;Haut 5;5.00
205;Pant 12;12.00
785;Pant 12;12.00`;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Importation CSV</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowFormat(!showFormat)}
                className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
              >
                <Info className="w-4 h-4 mr-2" />
                <span>{showFormat ? 'Masquer format' : 'Voir format'}</span>
              </button>
              {(file || textAreaContent) && (
                <button
                  onClick={handleReset}
                  className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  <X className="w-4 h-4 mr-2" />
                  <span>Réinitialiser</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showFormat && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-3 text-blue-800">Format CSV attendu</h3>
          <p className="mb-3">
            Entrez directement les valeurs au format suivant:
          </p>
          <ul className="list-disc ml-6 mb-4 space-y-1">
            <li><strong>client_number</strong> - Le numéro du client</li>
            <li><strong>description</strong> - Description des articles</li>
            <li><strong>total_price</strong> - Prix total</li>
          </ul>
          <div className="bg-white p-4 rounded border border-blue-200 mb-4">
            <h4 className="font-medium mb-2 text-blue-700">Exemple:</h4>
            <pre className="text-sm bg-gray-50 p-3 rounded overflow-auto">{csvTemplate}</pre>
          </div>
          <p className="text-sm text-gray-600">
            Le séparateur doit être un point-virgule (;).
          </p>
        </div>
      )}

      {/* Sélection de la méthode d'importation */}
      {!file && data.length === 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setImportMethod('file')}
              className={`px-4 py-2 rounded-md flex-1 ${
                importMethod === 'file' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Importer un fichier CSV
            </button>
            <button
              onClick={() => setImportMethod('textarea')}
              className={`px-4 py-2 rounded-md flex-1 ${
                importMethod === 'textarea' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Coller des données CSV
            </button>
          </div>
        </div>
      )}

      {!file && data.length === 0 && importMethod === 'file' ? (
        <div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-500 transition-colors"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
          />
          <div className="flex flex-col items-center">
            <Upload className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Déposez votre fichier CSV ici</h3>
            <p className="text-gray-500 mb-4">ou cliquez pour sélectionner un fichier</p>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors">
              Parcourir
            </button>
          </div>
        </div>
      ) : !file && data.length === 0 && importMethod === 'textarea' ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Coller vos données CSV</h3>
          <textarea
            className="w-full h-64 p-4 border border-gray-300 rounded-md mb-4"
            placeholder={csvTemplate}
            value={textAreaContent}
            onChange={(e) => setTextAreaContent(e.target.value)}
          ></textarea>
          <div className="flex justify-end">
            <button
              onClick={parseTextAreaContent}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              disabled={isLoading}
            >
              {isLoading ? 'Analyse en cours...' : 'Analyser les données'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-6">
              <FileText className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <h3 className="font-semibold">
                  {file ? file.name : 'Données importées'}
                </h3>
                {file && (
                  <p className="text-sm text-gray-500">{Math.round(file.size / 1024)} Ko</p>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <>
                {errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-red-700 mb-2">
                          {errors.length} erreur(s) détectée(s)
                        </h4>
                        <ul className="text-sm text-red-600 space-y-1 max-h-40 overflow-y-auto">
                          {errors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {data.length > 0 && (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold">
                        Aperçu des données ({data.length} lignes)
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {data.filter(item => item.validated).length} sélectionné(s)
                        </span>
                        <button
                          onClick={handleSendToWooCommerce}
                          disabled={data.filter(item => item.validated).length === 0 || processingStatus.isProcessing}
                          className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          <span>Envoyer à WooCommerce</span>
                        </button>
                      </div>
                    </div>

                    {processingStatus.isProcessing && (
                      <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">Progression</span>
                          <span className="text-sm font-medium">
                            {processingStatus.current} / {processingStatus.total}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-blue-600 h-2.5 rounded-full" 
                            style={{ width: `${(processingStatus.current / processingStatus.total) * 100}%` }}
                          ></div>
                        </div>
                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                          <span>Réussis: {processingStatus.success}</span>
                          <span>Échecs: {processingStatus.failed}</span>
                        </div>
                      </div>
                    )}

                    <div className="overflow-x-auto border rounded">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Sélection
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Client
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Description
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Prix
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Statut
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {data.map(row => (
                            <tr key={row.id} className={!row.validated ? 'bg-gray-50' : ''}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button 
                                  onClick={() => toggleRowValidation(row.id)}
                                  className={`p-1 rounded-full ${row.validated ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                                  title={row.validated ? 'Désélectionner' : 'Sélectionner'}
                                >
                                  {row.validated ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {row.client_number}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                <div className="whitespace-pre-line">
                                  {row.items.join('\n')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {row.total_price}€
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {row.status === 'success' && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Envoyé
                                  </span>
                                )}
                                {row.status === 'error' && (
                                  <div>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Échec
                                    </span>
                                    {row.error && (
                                      <p className="text-xs text-red-500 mt-1">{row.error}</p>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}